// src/components/mdx/client/ImageCarousel.client.tsx

'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from "motion/react"
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react'
import { cn } from '~/lib/utils'

/**
 * Represents a touch point with x and y coordinates.
 * 
 * @typedef {Object} TouchPoint
 * @property {number} clientX - The x-coordinate of the touch point.
 * @property {number} clientY - The y-coordinate of the touch point.
 */
interface TouchPoint {
  clientX: number
  clientY: number
}

/**
 * Represents an image to be displayed in the carousel.
 * 
 * @typedef {Object} CarouselImage
 * @property {string} src - Source URL of the image.
 * @property {string} alt - Alternative text for the image.
 * @property {string} [caption] - Optional caption text.
 * @property {number} [width] - Width of the image in pixels.
 * @property {number} [height] - Height of the image in pixels.
 * @property {boolean} [priority] - Whether to prioritize loading this image.
 */
interface CarouselImage {
  /** Source URL of the image */
  src: string
  /** Alternative text for the image */
  alt: string
  /** Optional caption text */
  caption?: string
  /** Width of the image in pixels */
  width?: number 
  /** Height of the image in pixels */
  height?: number
  /** Whether to prioritize loading this image */
  priority?: boolean
}

/**
 * Props for the ImageCarousel component.
 * 
 * @typedef {Object} ImageCarouselProps
 * @property {CarouselImage[]} images - Array of image objects to display.
 * @property {string} [className] - Additional CSS classes.
 * @property {boolean} [autoPlay=false] - Whether to automatically advance slides.
 * @property {number} [autoPlayInterval=5000] - Interval between auto-advancing slides in ms.
 * @property {boolean} [showThumbnails=true] - Whether to show thumbnail navigation.
 * @property {number} [transitionDuration=0.2] - Transition duration in seconds.
 * @property {number} [maxHeight=600] - Maximum height of the carousel in pixels.
 * @property {string} [objectFit="contain"] - How images should be sized: "cover" (fills space) or "contain" (shows entire image).
 */
export interface ImageCarouselProps {
  /** Array of image objects to display */
  images: CarouselImage[]
  /** Additional CSS classes */
  className?: string
  /** Whether to automatically advance slides */
  autoPlay?: boolean
  /** Interval between auto-advancing slides in ms */
  autoPlayInterval?: number
  /** Whether to show thumbnail navigation */
  showThumbnails?: boolean
  /** Transition duration in seconds */
  transitionDuration?: number
  /** Maximum height of the carousel in pixels */
  maxHeight?: number
  /** How images should be sized: "cover" (fills space) or "contain" (shows entire image) */
  objectFit?: "cover" | "contain"
}

/**
 * Custom hook to handle touch and swipe gestures for carousel navigation.
 * 
 * @param {Function} onSwipeLeft - Callback function to execute on left swipe.
 * @param {Function} onSwipeRight - Callback function to execute on right swipe.
 * @param {number} [threshold=50] - Minimum distance in pixels to trigger a swipe.
 * @returns {Object} Object containing touch event handlers.
 * @returns {Function} .handleTouchStart - Function to handle touch start events.
 * @returns {Function} .handleTouchEnd - Function to handle touch end events.
 */
function useCarouselGestures(
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  threshold = 50
): {
  handleTouchStart: (e: React.TouchEvent | React.MouseEvent) => void
  handleTouchEnd: (e: React.TouchEvent | React.MouseEvent) => void
} {
  const touchStart = useRef<TouchPoint | null>(null)
  
  const handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e && e.touches[0]) {
      touchStart.current = {
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY
      }
    } else if ('clientX' in e) {
      touchStart.current = {
        clientX: e.clientX,
        clientY: e.clientY
      }
    }
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!touchStart.current) return

    let endX: number
    if ('changedTouches' in e && e.changedTouches[0]) {
      endX = e.changedTouches[0].clientX
    } else if ('clientX' in e) {
      endX = e.clientX
    } else {
      return
    }

    const diff = touchStart.current.clientX - endX

    if (Math.abs(diff) > threshold) {
      if (diff > 0) onSwipeLeft()
      else onSwipeRight()
    }
    
    touchStart.current = null
  }, [onSwipeLeft, onSwipeRight, threshold])

  return { handleTouchStart, handleTouchEnd }
}

/**
 * Scrollable carousel component for displaying multiple images with animations and navigation.
 * 
 * Features include:
 * - Touch/swipe gesture support
 * - Keyboard navigation
 * - Optional auto-play functionality
 * - Thumbnail navigation
 * - Accessibility support
 * - Responsive design
 * - Intelligent handling of mixed aspect ratios
 * 
 * @param {ImageCarouselProps} props - Component props
 * @param {CarouselImage[]} props.images - Array of image objects to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.autoPlay=false] - Whether to automatically advance slides
 * @param {number} [props.autoPlayInterval=5000] - Interval between auto-advancing slides in ms
 * @param {boolean} [props.showThumbnails=true] - Whether to show thumbnail navigation
 * @param {number} [props.transitionDuration=0.2] - Transition duration in seconds
 * @param {number} [props.maxHeight=600] - Maximum height of the carousel in pixels
 * @param {string} [props.objectFit="contain"] - How images should be sized: "cover" or "contain"
 * @returns {JSX.Element|null} The rendered carousel component or null if no images
 * @example
 * // Basic usage
 * <ImageCarousel 
 *   images={[
 *     { src: "/image1.jpg", alt: "Description of image 1" },
 *     { src: "/image2.jpg", alt: "Description of image 2", caption: "Optional caption" }
 *   ]} 
 * />
 * 
 * // With auto-play and custom max height
 * <ImageCarousel 
 *   images={imageArray}
 *   autoPlay={true}
 *   maxHeight={800}
 *   objectFit="contain"
 * />
 */
export function ImageCarousel({
  images,
  className,
  autoPlay = false,
  autoPlayInterval = 5000,
  showThumbnails = true,
  transitionDuration = 0.2,
  maxHeight = 600,
  objectFit = "contain"
}: ImageCarouselProps): JSX.Element | null {
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [imageAspectRatios, setImageAspectRatios] = useState<Record<number, number>>({})
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({})
  
  // Detect container width for responsive calculations
  useEffect(() => {
    const currentContainer = containerRef.current
    if (!currentContainer) return
    
    const updateWidth = (): void => {
      if (currentContainer) {
        setContainerWidth(currentContainer.clientWidth)
      }
    }
    
    // Set initial width
    updateWidth()
    
    // Update on resize
    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(currentContainer)
    
    return () => {
      // Use captured ref value to prevent stale refs in cleanup
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer)
      }
    }
  }, [])
  
  // Preload image dimensions to avoid layout shifts during transitions
  useEffect(() => {
    const loadImageDimensions = async (): Promise<void> => {
      const ratios: Record<number, number> = {}
      const errors: Record<number, boolean> = {}
      
      for (let i = 0; i < images.length; i++) {
        const currentImage = images[i]
        if (currentImage?.width && currentImage?.height) {
          ratios[i] = currentImage.width / currentImage.height
        } else if (currentImage) {
          // If dimensions aren't provided, load the image to get dimensions
          try {
            const imageElement = new window.Image()
            imageElement.src = currentImage.src
            
            await new Promise<void>((resolve, reject) => {
              imageElement.onload = (): void => {
                if (imageElement.width && imageElement.height) {
                  ratios[i] = imageElement.width / imageElement.height
                } else {
                  // Fallback if dimensions still not available
                  ratios[i] = 16 / 9
                }
                errors[i] = false
                resolve()
              }
              imageElement.onerror = (): void => {
                ratios[i] = 16 / 9 // Fallback on error
                errors[i] = true
                reject(new Error("Failed to load image dimensions"))
              }
            })
          } catch (_error) {
            // Fallback to a default aspect ratio if loading fails
            ratios[i] = 16 / 9
            errors[i] = true
          }
        }
      }
      
      setImageAspectRatios(ratios)
      setImageErrors(errors)
    }
    
    void loadImageDimensions()
  }, [images])
  
  // Navigation handlers with proper type safety
  const navigate = useCallback((newIndex: number, dir: number): void => {
    if (isAnimating) return
    setIsAnimating(true)
    setDirection(dir)
    setActiveIndex(newIndex)
    setTimeout(() => setIsAnimating(false), transitionDuration * 1000)
  }, [isAnimating, transitionDuration])

  const handlePrev = useCallback(() => {
    if (!images?.length) return
    const newIndex = activeIndex === 0 ? images.length - 1 : activeIndex - 1
    navigate(newIndex, -1)
  }, [activeIndex, images, navigate])

  const handleNext = useCallback(() => {
    if (!images?.length) return
    const newIndex = activeIndex === images.length - 1 ? 0 : activeIndex + 1
    navigate(newIndex, 1)
  }, [activeIndex, images, navigate])

  // Touch/swipe handlers using custom hook
  const { handleTouchStart, handleTouchEnd } = useCarouselGestures(
    handleNext,
    handlePrev
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowLeft') handlePrev()
      if (e.key === 'ArrowRight') handleNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleNext, handlePrev])

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || isHovered || isAnimating) return
    const interval = setInterval(handleNext, autoPlayInterval)
    return () => clearInterval(interval)
  }, [autoPlay, autoPlayInterval, isHovered, isAnimating, handleNext])

  // This leaf is rendered ONLY on the client, after hydration, by makeClientSSR's
  // HydrationGate (the crawlable shadow is the server/no-JS output). It never
  // SSRs, so the old `if (!isMounted) return null` SSR gate is unnecessary here —
  // and harmful: it blanked for one paint right after the gate swapped in the
  // freshly-loaded chunk (#372 r3461232324). The shadow stays on screen until
  // this leaf is mounted; removing the gate lets the first client paint be the
  // real carousel, with no intermediate null.

  // Validate input
  if (!images?.length) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('ImageCarousel: No images provided')
    }
    return null
  }

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  }

  // Get current image with type safety
  const currentImage = images[activeIndex]
  if (!currentImage) return null

  // Determine if current image is portrait (height > width)
  const currentAspectRatio = imageAspectRatios[activeIndex]
  const isPortrait = currentAspectRatio ? currentAspectRatio < 1 : false
  
  // Calculate optimal dimensions based on aspect ratio
  const calcImageDimensions = (): { width: string; height: string; maxHeight: string } => {
    if (!currentAspectRatio || !containerWidth) {
      return {
        width: '100%',
        height: isPortrait ? `${maxHeight}px` : 'auto',
        maxHeight: `${maxHeight}px`
      }
    }
    
    if (isPortrait) {
      // For portrait images, constrain by height
      const calculatedHeight = Math.min(maxHeight, containerWidth / currentAspectRatio)
      const calculatedWidth = calculatedHeight * currentAspectRatio
      
      return {
        width: `${calculatedWidth}px`,
        height: `${calculatedHeight}px`,
        maxHeight: `${maxHeight}px`
      }
    } else {
      // For landscape images, use 100% width
      const calculatedHeight = containerWidth / currentAspectRatio
      
      return {
        width: '100%', // Full width
        height: `${Math.min(maxHeight, calculatedHeight)}px`,
        maxHeight: `${maxHeight}px`
      }
    }
  }
  
  const imageDimensions = calcImageDimensions()
  const hasImageError = imageErrors[activeIndex]
  
  return (
    <div 
      className={cn('relative group flex flex-col w-full max-w-4xl mx-auto', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      ref={containerRef}
      role="region"
      aria-label="Image carousel"
    >
      {/* Main carousel container - adaptive dimensions */}
      <div 
        className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseUp={handleTouchEnd}
        style={{
          minHeight: "300px",
          maxHeight: `${maxHeight}px`,
          height: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: `height ${transitionDuration}s ease-in-out`
        }}
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={activeIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: transitionDuration,
              ease: "easeInOut" as const
            }}
            className="flex items-center justify-center w-full h-full"
            style={{
              padding: 0 // Remove all padding
            }}
          >
            {/* Image wrapper with adaptive sizing */}
            <div 
              ref={imageContainerRef}
              className="relative"
              style={{
                width: imageDimensions.width,
                height: imageDimensions.height,
                transition: `width ${transitionDuration}s ease-in-out, height ${transitionDuration}s ease-in-out`,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 0 // Remove border radius
              }}
            >
              {hasImageError ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200 dark:bg-gray-700">
                  <ImageOff className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-gray-600 dark:text-gray-400 text-sm text-center px-4">
                    Image could not be loaded
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">
                    {currentImage.alt || "Missing image"}
                  </p>
                </div>
              ) : (
                <Image
                  src={currentImage.src}
                  alt={currentImage.alt ?? "Carousel image"}
                  width={800}
                  height={600}
                  className={cn(
                    "transition-transform duration-300",
                    "group-hover:scale-[1.02]"
                  )}
                  style={{ 
                    objectFit: objectFit,
                    objectPosition: "center",
                    width: "100%",
                    height: "100%"
                  }}
                  priority={currentImage.priority ?? activeIndex === 0}
                  sizes="(min-width: 1280px) 1200px, (min-width: 780px) 800px, 100vw"
                  quality={90}
                  onError={() => {
                    setImageErrors(prev => ({...prev, [activeIndex]: true}))
                  }}
                />
              )}
            </div>

            {/* Caption if available - positioned below the image */}
            {currentImage.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-black/70 p-4 z-10">
                <p className="text-white text-center">{currentImage.caption}</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className={cn(
                "absolute left-4 top-1/2 -translate-y-1/2 z-20",
                "p-2 rounded-full bg-black/50 backdrop-blur-sm",
                "hover:bg-black/75 transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-white/50"
              )}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={handleNext}
              className={cn(
                "absolute right-4 top-1/2 -translate-y-1/2 z-20",
                "p-2 rounded-full bg-black/50 backdrop-blur-sm",
                "hover:bg-black/75 transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-white/50"
              )}
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
            
            {/* Click areas for easier navigation */}
            <div 
              className="absolute left-0 top-0 bottom-0 w-1/4 z-10 cursor-pointer"
              onClick={handlePrev}
              aria-hidden="true"
            />
            <div 
              className="absolute right-0 top-0 bottom-0 w-1/4 z-10 cursor-pointer"
              onClick={handleNext}
              aria-hidden="true"
            />
          </>
        )}

        {/* Slide indicators - only show if thumbnails are disabled */}
        {images.length > 1 && !showThumbnails && (
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 z-20">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => navigate(index, index > activeIndex ? 1 : -1)}
                className={cn(
                  "transition-all duration-300",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                  index === activeIndex
                    ? "bg-white w-4 h-2 rounded-full"
                    : "bg-white/50 hover:bg-white/75 w-2 h-2 rounded-full"
                )}
                aria-label={`Go to image ${index + 1}`}
                aria-current={index === activeIndex ? "true" : "false"}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail navigation - positioned below the main carousel */}
      {images.length > 1 && showThumbnails && (
        <div className="flex justify-center mt-4 gap-2 pb-4 flex-wrap">
          {images.map((image, index) => (
              <button
                key={index}
                onClick={() => navigate(index, index > activeIndex ? 1 : -1)}
                className={cn(
                "relative h-16 w-16 overflow-hidden rounded focus:outline-none",
                  "transition-all duration-200 hover:opacity-90",
                  "focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                index === activeIndex
                  ? "ring-2 ring-blue-600 ring-offset-2"
                    : "opacity-70"
                )}
                aria-label={`Go to image ${index + 1}`}
              aria-current={index === activeIndex ? "true" : "false"}
              >
                {imageErrors[index] ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                    <ImageOff className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                ) : (
                  <Image
                    src={image.src}
                    alt={image.alt || `Thumbnail ${index + 1}`}
                    width={64}
                    height={64}
                    className="object-cover w-full h-full"
                    sizes="64px"
                    onError={() => {
                      setImageErrors(prev => ({...prev, [index]: true}))
                    }}
                  />
                )}
              </button>
          ))}
        </div>
      )}

      {/* Accessibility info */}
      <div className="sr-only" aria-live="polite">
        Showing image {activeIndex + 1} of {images.length}
        {currentImage.caption && `: ${currentImage.caption}`}
      </div>
    </div>
  )
}

export default ImageCarousel
